import React, { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  FaCube,
  FaMagic,
  FaFilm,
  FaLightbulb,
  FaTools,
  FaCertificate,
  FaUsers,
  FaClock,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import { CheckCircle } from "lucide-react";

const VIDEOS = {
  preview1: "https://www.w3schools.com/html/mov_bbb.mp4",
  preview2: "https://www.w3schools.com/html/movie.mp4",
};

const ThreeDVideoEditing = () => {
  useEffect(() => {
    AOS.init({ duration: 900, once: true });
    window.scrollTo(0, 0);
  }, []);

  const features = [
    {
      icon: <FaCube className="text-purple-600 text-3xl" />,
      title: "3D Modeling Basics",
      desc: "Learn to create realistic 3D objects and environments.",
      video: VIDEOS.preview1,
    },
    {
      icon: <FaMagic className="text-green-600 text-3xl" />,
      title: "Visual Effects",
      desc: "Add explosions, particles, and cinematic 3D effects.",
      video: VIDEOS.preview2,
    },
    {
      icon: <FaFilm className="text-blue-600 text-3xl" />,
      title: "Camera & Lighting",
      desc: "Master angles, lighting, and rendering techniques.",
    },
    {
      icon: <FaLightbulb className="text-orange-600 text-3xl" />,
      title: "Advanced Animation",
      desc: "Animate 3D objects, characters, and cinematic sequences.",
    },
  ];

  const workflow = [
    "Model 3D objects and design your scenes.",
    "Apply textures, materials, and shaders.",
    "Set up cameras, lights, and render settings.",
    "Animate objects and apply effects.",
    "Render your final video in high quality.",
  ];

  const tools = [
    "Blender",
    "Autodesk Maya",
    "Cinema 4D",
    "Adobe After Effects",
    "3ds Max",
  ];

  const cardBgColors = [
    "#FFF3E0", // mild orange
    "#E8F5E9", // mild green
    "#E0F7FA", // mild blue
    "#FCE4EC", // mild pink
    "#EDE7F6", // mild purple
    "#E0F2F1", // mild teal
  ];

  return (
    <div className="bg-white text-black min-h-screen overflow-hidden">
      {/* Custom CSS */}
      <style>
        {`
          @keyframes shake {
            0% { transform: translate(0, 0) rotate(0deg); }
            20% { transform: translate(-2px, 2px) rotate(-1deg); }
            40% { transform: translate(2px, -2px) rotate(1deg); }
            60% { transform: translate(-2px, 2px) rotate(0deg); }
            80% { transform: translate(2px, -1px) rotate(1deg); }
            100% { transform: translate(0, 0) rotate(0deg); }
          }
          .shake-hover:hover {
            animation: shake 0.5s ease-in-out;
          }
        `}
      </style>

      {/* Hero */}
      <section
        className="relative flex flex-col items-center justify-center text-center h-[85vh] bg-cover bg-center"
        style={{
          backgroundImage:
            "url('/assets/video-edit.webp')",
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="relative z-10 text-white px-4">
          <h1 className="text-5xl sm:text-6xl font-extrabold drop-shadow-lg">
            3D Video Editing Masterclass
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-lg text-gray-200">
            Dive into the world of 3D video editing and create cinematic
            experiences with advanced effects and animations.
          </p>
          <Link
            to="/contact"
            className="mt-6 inline-block px-8 py-4 bg-purple-600 text-white font-semibold rounded-full shadow-lg hover:bg-purple-700 transition transform hover:scale-105 animate-pulse"
          >
            Enroll Now
          </Link>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-center mb-12" data-aos="fade-up">
          Core Skills You Will Master
        </h2>
        <div className="grid lg:grid-cols-2 gap-12">
          {features.map((f, i) => (
            <div
              key={i}
              className="shake-hover relative flex flex-col md:flex-row items-center rounded-xl shadow-xl transition transform p-6 group"
              data-aos={i % 2 === 0 ? "fade-right" : "fade-left"}
              data-aos-delay={i * 150}
              style={{ backgroundColor: cardBgColors[i % cardBgColors.length] }}
            >
              <div className="flex-shrink-0 mb-4 md:mb-0 md:mr-6 animate-shake">
                {f.icon}
              </div>
              <div className="relative">
                <h3 className="font-semibold text-xl mb-2">{f.title}</h3>
                <p className="text-gray-700 text-sm">{f.desc}</p>
                {f.video && (
                  <video
                    src={f.video}
                    className="absolute top-0 left-0 w-full h-full opacity-0 group-hover:opacity-80 rounded-xl object-cover transition-opacity duration-500 z-0"
                    autoPlay
                    loop
                    muted
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Workflow */}
      <section className="py-16 max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
        <div data-aos="fade-left">
          <h3 className="text-2xl font-bold mb-6">Course Workflow</h3>
          <ul className="space-y-4">
            {workflow.map((step, i) => (
              <li key={i} className="flex gap-4 items-start">
                <span
                  className="flex items-center justify-center w-8 h-8 rounded-full font-bold animate-pulse"
                  style={{
                    backgroundColor: cardBgColors[i % cardBgColors.length],
                    color: "#1a1a1a", // dark icon color
                  }}
                >
                  <CheckCircle size={16} />
                </span>
                <p className="text-black">{step}</p>
              </li>
            ))}
          </ul>
        </div>
        <div data-aos="fade-right" className="relative">
          <img
            src="/assets/Ssm3tscuFsxyd4XERe.webp"
            alt="3D Workflow"
            className="rounded-xl shadow-lg"
          />
        </div>
      </section>

      {/* Tools You’ll Use */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h3 className="text-3xl font-bold mb-10" data-aos="fade-up">
            Tools You’ll Use
          </h3>
          <div className="flex flex-wrap justify-center gap-6 text-lg font-medium text-gray-700">
            {tools.map((tool, i) => (
              <span
                key={i}
                className="px-6 py-3 rounded-lg shadow-md hover:shadow-lg transition transform hover:-translate-y-1"
                data-aos="zoom-in"
                data-aos-delay={i * 100}
                style={{
                  backgroundColor: cardBgColors[i % cardBgColors.length],
                }}
              >
                <FaTools className="inline-block mr-2 text-purple-600 animate-pulse" />
                {tool}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-16 text-center max-w-5xl mx-auto px-6">
        <h3 className="text-3xl font-bold mb-8">Why Choose This Course?</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          <div
            className="p-6 rounded-xl shadow-md hover:shadow-lg transition"
            data-aos="fade-up"
            style={{ backgroundColor: cardBgColors[0] }}
          >
            <FaUsers className="text-purple-600 text-3xl mb-4 mx-auto" />
            <p>Perfect for beginners & creators exploring 3D editing.</p>
          </div>
          <div
            className="p-6 rounded-xl shadow-md hover:shadow-lg transition"
            data-aos="fade-up"
            data-aos-delay="100"
            style={{ backgroundColor: cardBgColors[1] }}
          >
            <FaClock className="text-green-600 text-3xl mb-4 mx-auto animate-shake" />
            <p>Flexible learning with self-paced modules.</p>
          </div>
          <div
            className="p-6 rounded-xl shadow-md hover:shadow-lg transition"
            data-aos="fade-up"
            data-aos-delay="200"
            style={{ backgroundColor: cardBgColors[2] }}
          >
            <FaCertificate className="text-blue-600 text-3xl mb-4 mx-auto" />
            <p>Certificate of Completion to boost your career.</p>
          </div>
        </div>
      </section>

      {/* Enroll CTA */}
      <section className="py-16 text-center" data-aos="fade-up">
        <h3 className="text-3xl font-bold mb-6">
          Ready to Create Stunning 3D Videos?
        </h3>
        <p className="text-gray-700 mb-8">
          Unlock your creative potential and bring your ideas to life in 3D.
        </p>
        <Link
          to="/contact"
          className="px-8 py-4 bg-purple-600 text-white font-semibold rounded-full shadow-lg hover:bg-purple-700 transition transform hover:scale-105 animate-pulse inline-block"
        >
          Enroll Now
        </Link>
      </section>
    </div>
  );
};

export default ThreeDVideoEditing;
